import type { AxiosResponse } from "axios";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef } from "react";

import { axios } from "@/client/libs/axios";

export type PaymentStatusData = {
  id: string;
  /** 0 → en attente · 1 → payé · 2 → annulé (contrat `Payment.status`). */
  status: number;
  /** Montant réglé, en FCFA. */
  Amount: number;
  rate: { name: string };
};

/** Statuts terminaux : plus aucun polling au-delà. */
const TERMINAL_STATUSES = [1, 2];

type StatusExtras = {
  /**
   * Force une vérification active côté serveur (« Vérifier maintenant »)
   * et rafraîchit immédiatement le statut.
   */
  verifyNow: () => Promise<void>;
  /** Une vérification est en cours. */
  isVerifying: boolean;
};

/**
 * Suivi du statut d'un paiement par SON propriétaire.
 *
 * `identifier` = id de paiement OU référence fournisseur OU notify_token — le
 * serveur résout les trois et cloisonne sur le compte connecté.
 *
 * Le serveur VÉRIFIE ACTIVEMENT le fournisseur si le paiement est encore en
 * attente : un webhook perdu ne bloque plus la page.
 *
 * Repli automatique : si l'identifiant est absent ou introuvable (chaque
 * agrégateur renvoie ses propres paramètres), on interroge le dernier paiement
 * en attente de l'utilisateur — plus jamais de page bloquée sans verdict.
 */
export const usePaymentStatus = (
  identifier?: string | null,
): ReturnType<typeof useQuery<PaymentStatusData>> & StatusExtras => {
  // Lu par la queryFn : permet un forçage ponctuel sans casser le cache
  // TanStack (la clé de requête ne dépend pas du forçage).
  const forceRef = useRef(false);

  const query = useQuery<PaymentStatusData>({
    queryKey: ["payments", "status", identifier ?? "latest"],
    retry: 1,
    queryFn: async () => {
      const params = forceRef.current ? { force: true } : undefined;

      if (identifier) {
        try {
          const response = await axios.get<PaymentStatusData>(`/payments/${identifier}/status`, {
            params,
          });
          return response.data;
        } catch (error) {
          const status = (error as AxiosResponse)?.status;
          // 404 → identifiant inconnu du serveur : on tente le repli.
          if (status !== 404) throw error;
        }
      }

      // Repli : dernier paiement en attente de l'utilisateur connecté.
      const fallback = await axios.get<PaymentStatusData>(`/payments/status/latest`, { params });
      return fallback.data;
    },
    refetchInterval: (query) => {
      // Paiement tranché, ou aucun paiement en attente → plus de polling.
      if (query.state.error) return false;
      const status = query.state.data?.status;
      return status === undefined || !TERMINAL_STATUSES.includes(status) ? 3_000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const verifyNow = useCallback(async () => {
    forceRef.current = true;
    try {
      await query.refetch();
    } finally {
      forceRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return { ...query, verifyNow, isVerifying: query.isFetching };
};
